import { IDatabase } from '../../../database';
import { FlowyFoundation } from '../../../flowy';
import { IFilledFormV2, IUserV2 } from '../../../types';
import { fileToBase64, gnov } from '../../../utils';
import { uploadFileS3 } from '../../../binders/daq/utils';
import axios from 'axios';

interface IParams {
  registers: IFilledFormV2[];
  useFileApiV2?: boolean;
  context: {
    apiUrl: string;
    user?: IUserV2;
    database?: IDatabase;
    foundation?: FlowyFoundation;
  };
}

interface IReturn {
  status: string;
  uploadedAttachmentIds: string[];
  uploadedAttachments: {
    attachmentId: string;
    extension: string;
  }[];
}

/**
 * Uploads the files from the registers to the server
 *
 * @param {IFilledFormV2[]} registers list of registers from where we're going to get the files
 *    info
 * @param {boolean} useFileApiV2 whether to use the new file api or not
 * @returns
 */
function uploadFiles({
  registers,
  useFileApiV2,
  context,
}: IParams): Promise<IReturn> {
  return new Promise<IReturn>(async resolve => {
    const { apiUrl, database, foundation, user } = context;
    let status = 'no_files';
    const uploadedAttachmentIds: string[] = [];
    const uploadedAttachments: {
      attachmentId: string;
      extension: string;
    }[] = [];

    const platformInterface = foundation?.getPlatformInterface();

    if (platformInterface && database) {
      const allSavedRegisters = registers;

      // We're going to look for saved registers with attachments, and upload them
      for (const register of allSavedRegisters) {
        let filesUrl = `${apiUrl}/v3/forms/${register.formId}/filled_forms/attachment`;

        if (register.formId === 'login-form-fid') {
          continue;
        }

        try {
          const form = (
            await database.get({
              type: 'forms',
              fid: register.formId,
            })
          ).formGizmo;
          const usernameGizmoUid = gnov(
            form,
            'advanced_options.form.auth.usernameGizmoUid'
          );
          if (usernameGizmoUid) {
            // TODO: we have to change this to upload directly to S3
            filesUrl = `${apiUrl}/public/forms/${register.formId}/filled_forms/attachment`;
          }
        } catch (e) {
          console.log('>>>>>>>>1.e: ', e);
        }

        const uploadFilesPromises: any[] = [];
        for (const fid in register.answers) {
          const gizmoData = register.answers[fid];

          for (const answerIndex in gizmoData) {
            const gD = gizmoData[answerIndex];
            const { attachmentId, extension } = gD;

            if (attachmentId && extension) {
              if (status === 'no_files') {
                status = 'all_files_uploaded';
              }

              // We ask the platformInterface for the file
              const saved = await platformInterface.fileGet({
                fileExtension: extension,
                fileFid: attachmentId,
                path: '',
              });

              if (saved?.file) {
                try {
                  if (!useFileApiV2) {
                    // await uploadFileS3({
                    //   file: saved.file,
                    //   attachmentId,
                    //   formId: register.formId,
                    //   filledFormId: register.filledFormId,
                    //   fId: fid,
                    //   answerIndex,
                    //   extension,
                    //   context: {
                    //     apiUrl,
                    //     user,
                    //   },
                    // });

                    uploadFilesPromises.push(
                      uploadFileS3({
                        file: saved.file,
                        attachmentId,
                        formId: register.formId,
                        filledFormId: register.filledFormId,
                        fId: fid,
                        answerIndex,
                        extension,
                        context: {
                          apiUrl,
                          user,
                        },
                      })
                    );
                  } else {
                    let headers = {};

                    if (user?.credentials) {
                      const credentials: any = user.credentials;
                      headers = {
                        ...credentials,
                      };
                    }

                    // If it exists we convert it to base64 and send it to the server
                    const base64 = await fileToBase64(saved.file);
                    const formData = new FormData();
                    formData.append('file', base64);
                    formData.append('attachmentId', attachmentId);
                    formData.append('formId', register.formId);
                    formData.append('filledFormId', register.filledFormId);
                    formData.append('answerIndex', answerIndex);
                    formData.append('fId', fid);
                    formData.append('extension', extension);

                    await axios.post(filesUrl, formData, {
                      headers: {
                        ...headers,
                        'Content-Type': 'multipart/form-data',
                      },
                    });
                  }

                  uploadedAttachmentIds.push(attachmentId);
                  uploadedAttachments.push({
                    attachmentId,
                    extension,
                  });
                } catch (e) {
                  status = 'error_uploading_files';
                }
              }
            }
          }
        }

        if (uploadFilesPromises.length) {
          try {
            await Promise.all(uploadFilesPromises);
          } catch (e) {
            console.log('>>>>>>>>uploadFiles.all->e: ', e);
            status = 'error_uploading_files';
          }
        }
      }
    }

    resolve({
      status,
      uploadedAttachmentIds,
      uploadedAttachments,
    });
  });
}

export default uploadFiles;
